import { Box, GridItem, ThemeSpaceVariable, useDisclosure } from "@caspeco/casper-ui-library";
import TimeSlot from "components/selection-components/select-time/time-set/time-slot";
import WaitlistModal from "components/wait-list/wait-list-modal";
import WaitListTimeSlot from "components/wait-list/wait-list-time-slot";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import { useTimeSlotFilters } from "hooks/use-time-slot-filters";
import React, { useState } from "react";
import { Time } from "store/store-types";
import { TimeSlot as TimeSlotType } from "types/time-slot";
import { WebRule } from "types/web-rule";
import { getSortedHourKeys, groupedTimes } from "utils/booking";
import TextRow from "./text-row";
import TimeSetComment from "./time-set-comment";
import TimeSetTitle from "./time-set-title";
import TimeSlotGrid from "./time-slot-grid";
interface TimeSetProps {
    timeSet: WebRule;
    selectedTime?: TimeSlotType;
    onTimeClickOverride?: (time: TimeSlotType) => void;
}

function TimeSet(props: TimeSetProps) {
    const state = useBookingStateContext();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const { setState, handleCreateBooking } = useBookingActionsContext();

    const [selectedWaitListTimes, setSelectedWaitListTimes] = useState<TimeSlotType[]>([]);

    const handleWaitlistClick = (time: TimeSlotType) => {
        if (!isOpen) {
            setState?.((prevState) => {
                return {
                    ...prevState,
                    isWaitList: true,
                };
            });

            onOpen();
        }

        const hasStartTime = selectedWaitListTimes.some((selectedTime) =>
            selectedTime.start.isSame(time.start)
        );

        let newSelectedTimes: TimeSlotType[];

        if (hasStartTime) {
            newSelectedTimes = selectedWaitListTimes.filter(
                (selectedTime) => !selectedTime.start.isSame(time.start)
            );
        } else {
            newSelectedTimes = [...selectedWaitListTimes, time];
        }

        setSelectedWaitListTimes(newSelectedTimes);

        if (newSelectedTimes.length === 0) {
            onClose();
            setState?.((prevState) => {
                return {
                    ...prevState,
                    isWaitList: false,
                };
            });
        }
    };

    const handleWaitlistModalClose = () => {
        onClose();
        setSelectedWaitListTimes([]);
    };

    const handleWaitlistContinue = () => {
        const stateWithTimes = {
            ...state,
            times: selectedWaitListTimes as Time[],
        };

        handleCreateBooking?.(stateWithTimes);
    };

    function getSlotsAsSortedGridItems(times: TimeSlotType[]) {
        const itemList: React.ReactNode[] = [];

        let slotsFilledInCurrentRow = 0;
        let itemCount = 1;

        const magicNumber = 4;

        const groupedTimesByHour = groupedTimes(times);
        const hourKeys = getSortedHourKeys(Object.keys(groupedTimesByHour));

        hourKeys?.forEach((hour, index) => {
            const timesThisHour = groupedTimesByHour[hour];
            const nextHourTimes = groupedTimesByHour[hourKeys[index + 1]] || [];

            timesThisHour?.forEach((time: TimeSlotType, timeIndex: number) => {
                const row = Math.ceil(itemCount / magicNumber);
                const column = itemCount % magicNumber;
                const isWaitListSlot = waitListTimes.some(
                    (waitListTime) =>
                        waitListTime.ruleId === time.ruleId && waitListTime.start.isSame(time.start)
                );
                const isBookableSlot = bookableTimes.some(
                    (bookableTime) =>
                        bookableTime.ruleId === time.ruleId && bookableTime.start.isSame(time.start)
                );

                itemList.push(
                    <GridItem
                        key={`time-${time.ruleId}-${time.start.format("HHmm")}`}
                        gridColumn={column}
                        gridRow={row}
                    >
                        {isWaitListSlot && !isBookableSlot ? (
                            <WaitListTimeSlot
                                onTimeClick={handleWaitlistClick}
                                selectedTimes={selectedWaitListTimes}
                                time={time}
                            />
                        ) : (
                            <TimeSlot
                                key={`webBookingTime-${time.ruleId}-${time.start.format("HHmm")}`}
                                time={time}
                                timeSet={props.timeSet}
                                isSelected={props.selectedTime?.start.isSame(time.start)}
                                onTimeClickOverride={props.onTimeClickOverride}
                            />
                        )}
                    </GridItem>
                );

                slotsFilledInCurrentRow = itemCount % magicNumber;
                const isLastTimeInHour = timeIndex === timesThisHour.length - 1;
                if (isLastTimeInHour) {
                    const remainingSlotsInCurrentRow = magicNumber - slotsFilledInCurrentRow;

                    if (!(nextHourTimes.length <= remainingSlotsInCurrentRow)) {
                        itemCount += remainingSlotsInCurrentRow;
                    }
                }
                itemCount++;
            });
        });

        return itemList;
    }

    const timeSet = props.timeSet;

    const { bookableTimes, waitListTimes } = useTimeSlotFilters(timeSet.times);
    const isMatchingSlot = (slots: TimeSlotType[], time: TimeSlotType) =>
        slots.some((slot) => slot.ruleId === time.ruleId && slot.start.isSame(time.start));
    const isBookableSlot = (time: TimeSlotType) => isMatchingSlot(bookableTimes, time);
    const isWaitListSlot = (time: TimeSlotType) =>
        isMatchingSlot(waitListTimes, time) && !isBookableSlot(time);
    const displayTimes = timeSet.times.filter(
        (time) => isBookableSlot(time) || isWaitListSlot(time)
    );

    const timeSlots = getSlotsAsSortedGridItems(displayTimes);

    if (timeSet.times.length < 1 && timeSet.isTextRow === false) return null;

    if (timeSet.isTextRow && !timeSet.isClosed) {
        return <TextRow title={timeSet.title} comment={timeSet.comment} />;
    }

    const noAvailableSlots = timeSlots.length === 0;
    if (noAvailableSlots) return null;

    return (
        <>
            <Box mb={ThemeSpaceVariable.Large}>
                <TimeSetTitle title={timeSet.title} />
                {timeSet.comment && <TimeSetComment comment={timeSet.comment} />}
                <TimeSlotGrid timeSlots={timeSlots} />
            </Box>
            <WaitlistModal
                isOpen={isOpen}
                handleWaitlistContinue={handleWaitlistContinue}
                handleWaitlistModalClose={handleWaitlistModalClose}
                waitListTimes={waitListTimes}
                selectedTimes={selectedWaitListTimes}
                onTimeClick={handleWaitlistClick}
            />
        </>
    );
}

export default React.memo(TimeSet);
